// import React from 'react';
// import { Paper, Typography, Box, FormControl, InputLabel, Select, MenuItem, Collapse, IconButton } from '@mui/material';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// // Interface for SelectChangeEvent
// type SelectChangeEvent = import('@mui/material').SelectChangeEvent<string>;

// interface CollapsibleSelectFilterProps {
//   name: string;
//   label: string;
//   value: string;
//   mockOptions: string[];
//   handleChange: (event: SelectChangeEvent) => void;
//   // We'll expose the collapsed state control for initial setup if needed
//   defaultOpen?: boolean;
// }

// const CollapsibleSelectFilter: React.FC<CollapsibleSelectFilterProps> = ({
//   name,
//   label,
//   value,
//   mockOptions,
//   handleChange,
//   defaultOpen = false,
// }) => {
//   // Local state for collapse control
//   const [open, setOpen] = React.useState(defaultOpen);

//   return (
//     <Box sx={{ mb: 1, border: '1px solid #eee', borderRadius: 1 }}>
//       {/* --- Filter Header (Toggle) --- */}
//       <Box
//         sx={{
//           display: 'flex',
//           justifyContent: 'space-between',
//           alignItems: 'center',
//           p: 1,
//           cursor: 'pointer',
//           bgcolor: '#f5f5f5', // Light background for header
//           borderBottom: open ? '1px solid #eee' : 'none',
//         }}
//         onClick={() => setOpen(!open)}
//       >
//         <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
//           {label}
//         </Typography>
//         <IconButton size="small">
//           {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
//         </IconButton>
//       </Box>

//       {/* --- Collapsible Content (The Select Component) --- */}
//       <Collapse in={open}>
//         <Box sx={{ p: 1, pt: 0 }}>
//           <FormControl fullWidth size="small" variant="outlined">
//             {/* <InputLabel>{label}</InputLabel> */}
//             <Select name={name} value={value} onChange={handleChange}>
//               {mockOptions.map((option) => (
//                 <MenuItem key={option} value={option}>
//                   {option}
//                 </MenuItem>
//               ))}
//             </Select>
//           </FormControl>
//         </Box>
//       </Collapse>
//     </Box>
//   );
// };

// export default CollapsibleSelectFilter;
import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  FormControl,
  Menu,
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { useAppState } from 'store/Provider';
import { selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';
import { changePageAction } from 'store/actions/dashboardActions';

interface OptionType {
  id: string | number;
  name: string;
}

interface CollapsibleSelectFilterProps {
  name: string;
  apiKeyname: string;
  label: string;
  disabled: boolean;
  mockOptions: OptionType[];
  value: OptionType[];
  onChange: (selectedValues: OptionType[]) => void;
  clearPerticularFilters: (apiKeyname: string) => void;
}

const CollapsibleSelectFilter: React.FC<CollapsibleSelectFilterProps> = ({
  name,
  apiKeyname,
  label,
  disabled,
  mockOptions,
  value,
  onChange,
  clearPerticularFilters,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const { dispatch } = useAppState();
  const [checkedOptions, setCheckedOptions] = useState<Record<string | number, boolean>>({});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Parse initial values
  useEffect(() => {
    const initial: Record<string | number, boolean> = {};
    let selectedIds: Array<string | number> = [];
    if (value === 'ALL') {
      // Select all checkboxes if value is 'ALL'
      mockOptions?.forEach((opt) => {
        initial[opt.id] = true;
      });
      setCheckedOptions(initial);
      setIsSelectAllChecked(true);
    } else {
      if (Array.isArray(value)) {
        selectedIds = value.map((v) => v.id);
      }
      mockOptions?.forEach((opt) => {
        initial[opt.id] = selectedIds.includes(opt.id);
      });
      setCheckedOptions(initial);
      setIsSelectAllChecked(selectedIds.length === mockOptions?.length);
    }
  }, [mockOptions, value]);

  const updateSelectAllState = (updated: Record<string | number, boolean>) => {
    const all = mockOptions?.length > 0 && mockOptions?.every((opt) => updated[opt.id]);
    setIsSelectAllChecked(all);
  };

  const handleCheckboxChange = (key: string | number, checked: boolean) => {
    // dispatch(selectFilterAction({ [key]: [value] }));
    setCheckedOptions((prev) => {
      const updated = { ...prev, [key]: checked };
      updateSelectAllState(updated);
      return updated;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    const updated: Record<string | number, boolean> = {};
    mockOptions?.forEach((opt) => {
      updated[opt.id] = checked;
    });
    setCheckedOptions(updated);
    setIsSelectAllChecked(checked);
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setSearch('');
    setOpen(false);
  };

  const handleApply = () => {
    if (isSelectAllChecked) {
      dispatch(selectFilterAction({ [apiKeyname]: ['ALL'] }));
      onChange('ALL');
    } else {
      const selected = mockOptions?.filter((opt) => checkedOptions[opt.id]);
      if (selected.length === 1) {
        dispatch(selectFilterAction({ [apiKeyname]: [selected[0].id.toString()] }));
      } else {
        dispatch(selectFilterAction({ [apiKeyname]: selected.map((s) => s.id.toString()) }));
      }
      onChange(selected);
    }
    handleClose();
  };

  const perticularFieldClear = () => {
    dispatch(selectFilterAction({ [apiKeyname]: ['ALL'] }));
    onChange('ALL');
  };

  const handleClear = (field: string, apiKeyname: string) => {
    onChange([]);
    setCheckedOptions({});
    clearPerticularFilters(apiKeyname);
    dispatch(selectFilterRemoveAction({ [field]: 'ALL' }));
    dispatch(changePageAction(0));
    setIsSelectAllChecked(true);
    perticularFieldClear();
    handleClose();
  };

  // Fix displayValue for array or string, and show 'ALL' if selected
  let displayValue = '';
  if (value === 'ALL') {
    displayValue = 'ALL';
  } else if (Array.isArray(value)) {
    displayValue = value.map((v) => v.name).join(', ');
  }

  // Search state
  const [search, setSearch] = useState('');
  const filteredOptions = mockOptions?.filter((opt) =>
    String(opt?.name ?? '') // Convert to string and fallback to empty string if null
      .toLowerCase()
      .includes(search?.toLowerCase() ?? '')
  );

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth>
        <label htmlFor={`collapsible-select-filter-${name}`}>{label}</label>

        <TextField
          id={`collapsible-select-filter-${name}`}
          value={displayValue}
          onClick={handleOpen}
          readOnly
          fullWidth
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {displayValue && displayValue !== 'ALL' && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear(name);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" onClick={handleOpen}>
                  <ArrowDropDownIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      {/* Dropdown Menu */}
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {/* Search Input */}
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </Box>
        {/* ALL Checkbox */}
        <MenuItem onMouseDown={stop} sx={{ borderBottom: '1px solid #ddd' }}>
          <FormControlLabel
            control={<Checkbox checked={isSelectAllChecked} onChange={(e) => handleSelectAll(e.target.checked)} />}
            label="ALL"
          />
        </MenuItem>

        {/* Options with scrollable container */}
        <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
          {filteredOptions?.map((opt) => (
            <MenuItem key={opt.id} onMouseDown={stop} sx={{ minHeight: 25, py: 0.5, width: 340 }} disabled={disabled}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkedOptions[opt.id] || false}
                    onChange={(e) => handleCheckboxChange(opt.id, e.target.checked)}
                    size="small"
                    disabled={disabled}
                  />
                }
                label={opt.name}
                sx={{ '.MuiFormControlLabel-label': { fontSize: '0.95rem' }, m: 0 }}
              />
            </MenuItem>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }} onMouseDown={stop}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default CollapsibleSelectFilter;
